/**
 * Dex grid filtering + view controls (Push 2 / v1.4.3 b2).
 *
 * Pure helpers that turn the full National Dex roster plus the user's
 * recorded per-variant state into the list of dex numbers a grid should
 * render. Kept framework-free so it can be unit-tested with plain `tsx`
 * (see dexGridFilter.test.ts).
 *
 * Background: the Pokédex-first page previously hardcoded a 151-entry
 * Kanto-only default grid, so non-Kanto species never appeared until a
 * scan recorded them. The full 1025-entry roster (speciesNames.ts), the
 * silhouette atlas (1025 tiles), and region helpers (types.ts) all already
 * exist — this module wires them together and adds region / status /
 * search / sort controls on top.
 */

import { regionForId, typesFor, type Region, type PokeType } from "./types";
import { speciesName, SPECIES_NAMES } from "../data/speciesNames";
import {
  isSparseVariant,
  type PokedexVariant,
  type CellState,
} from "./pogoPokedexVariantClassifier";
import { curatedRosterFor } from "./pogoAvailability";
import { isVariantAvailable } from "./variantAvailability";
import { NATIONAL_DEX_TOTAL } from "./constants";
import { hasAltForms, hasCostumes } from "./formCatalog";
import { expandToFamilies, isBaseOrBabyEvolution } from "./evolutionFamily";
import { isRegionExclusive } from "./regionExclusive";
import { matchesSpeciesClasses, type SpeciesClass } from "./speciesClass";

/** Largest dex number the roster / silhouette atlas supports. */
export const NATIONAL_DEX_MAX = NATIONAL_DEX_TOTAL;

/**
 * Region filter keys. Mirrors the canonical REGIONS taxonomy plus "all".
 *
 * Push 4 / v1.4.3 b4: "Unknown" is no longer a user-visible region option.
 * With the Hisui id-set fix in types.ts, regionForId() now resolves every
 * id in 1..1025, so the old "unknown" bucket can never be non-empty for a
 * real roster. The type still allows "unknown" so any stray id (e.g. an
 * out-of-range scan) is grouped safely without crashing — but it is
 * excluded from REGION_FILTER_ORDER so it never renders as a chip.
 */
export type RegionFilter = "all" | Region | "unknown";

export const REGION_FILTER_ORDER: RegionFilter[] = [
  "all", "kanto", "johto", "hoenn", "sinnoh", "unova",
  "kalos", "alola", "galar", "hisui", "paldea", "unidentified",
];

export const REGION_FILTER_LABELS: Record<RegionFilter, string> = {
  all: "All",
  kanto: "Kanto",
  johto: "Johto",
  hoenn: "Hoenn",
  sinnoh: "Sinnoh",
  unova: "Unova",
  kalos: "Kalos",
  alola: "Alola",
  galar: "Galar",
  hisui: "Hisui",
  paldea: "Paldea",
  // Meltan #808 / Melmetal #809 — GO's region-less "Unidentified" grouping.
  unidentified: "Unidentified",
  unknown: "Unknown",
};

/**
 * Status filter keys derived from dexStateStore fields:
 *   - caught   → recorded state === "present"
 *   - missing  → no record yet, OR recorded state !== "present"
 *
 * The "manual" / "scan" filters were removed (2026-07-18): dex writes are
 * last-write-wins and `source` no longer confers any meaning, so filtering by
 * HOW a cell was recorded surfaced a distinction the app no longer makes.
 */
export type StatusFilter = "all" | "caught" | "missing";

export const STATUS_FILTER_ORDER: StatusFilter[] = ["all", "caught", "missing"];

export const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
  all: "All",
  caught: "Caught",
  missing: "Missing",
};

/**
 * Availability filter: whether a species is actually obtainable for the
 * CURRENT variant in Pokémon GO right now.
 *
 * This is variant-aware, not a global "is this Pokémon in GO" flag — the same
 * dex number can be released on the Pokemon (All) grid and unreleased on Shiny
 * (species is out, its shiny is not) or on Lucky (released but untradable). It
 * reuses isVariantAvailable() so this filter and the on-cell "locked" /
 * "unreleased" callouts can never disagree.
 *
 * Rosters fail open (isVariantAvailable returns true when unknown), so an
 * unknown species is treated as released and is never hidden from "missing".
 */
export type AvailabilityFilter = "all" | "released" | "unreleased";

export const AVAILABILITY_FILTER_ORDER: AvailabilityFilter[] = ["all", "released", "unreleased"];

export const AVAILABILITY_FILTER_LABELS: Record<AvailabilityFilter, string> = {
  all: "All",
  released: "Released",
  unreleased: "Unreleased",
};

/**
 * Forms filter: narrow the grid to species that carry extra dex entries.
 *
 *   all      — no filter.
 *   forms    — species with two or more real forms (Alolan/Galarian, Vivillon
 *              patterns, Unown letters, Spinda patterns, Deoxys modes …).
 *   costumes — species with at least one Pokémon GO event costume.
 *
 * Single-select rather than two toggles: costumes are a strict subset of "has
 * extra entries", so an AND of the two would mean "has both", which is a
 * question nobody asks, and an OR would just be "forms". Picking one axis value
 * keeps the summary line honest and matches Region / Sort.
 *
 * This exists to make the two rosters WORKABLE: the detail page is where a
 * trainer fills forms and costumes in, and until now there was no way to list
 * the species that have any.
 *
 * The `forms` chip said "Regional" for one day and is back to "Forms". In
 * Pokémon GO "regional" means REGION-LOCKED — Kangaskhan, a species you cannot
 * catch at home — and this roster is the unrelated one: species with an extra
 * FORM, which is mostly regional forms but also Unown, Spinda, Vivillon and
 * Deoxys, and every one of them catchable anywhere. Region-locked species are
 * their own axis now; see ExclusiveFilter below.
 */
export type FormFilter = "all" | "forms" | "costumes";

export const FORM_FILTER_ORDER: FormFilter[] = ["all", "forms", "costumes"];

export const FORM_FILTER_LABELS: Record<FormFilter, string> = {
  all: "Any",
  forms: "Forms",
  costumes: "Costume",
};

/**
 * Region-exclusive filter: the species Pokémon GO only spawns in part of the
 * world (Kangaskhan in Australia, Tauros in North America, Klefki in France).
 *
 *   all       — no filter.
 *   exclusive — only region-locked species.
 *   global    — only species with no region lock.
 *
 * This is the axis a dex filter could not previously express, and it is the one
 * that decides whether a gap is CLOSEABLE: "Missing + Released" already reads
 * as "the set you can still go and catch", and for forty-odd species that is
 * not true at all — no amount of walking will do it, only a trade or a flight.
 * Splitting them out makes both halves honest: the exclusives are the trade
 * list, and everything else is the walk list.
 *
 * The roster is hand-maintained (region locks are server-side geo config that
 * nothing publishes); see regionExclusive.ts for why, and for how to edit it.
 */
export type ExclusiveFilter = "all" | "exclusive" | "global";

export const EXCLUSIVE_FILTER_ORDER: ExclusiveFilter[] = ["all", "exclusive", "global"];

export const EXCLUSIVE_FILTER_LABELS: Record<ExclusiveFilter, string> = {
  all: "Any",
  exclusive: "Region locked",
  global: "Global",
};

/**
 * Species-class filter: Starter / Legendary / Mythical / Ultra Beast /
 * Pseudo-legendary (2026-09-01 owner ask).
 *
 * Multi-select (an empty array is "any"), because unlike Region or Stages the
 * classes are disjoint, so single-select would make "the rare stuff" — the
 * obvious query — impossible to ask. The rosters and the reasoning behind each
 * one live in lib/speciesClass.ts.
 */
export type { SpeciesClass } from "./speciesClass";

/**
 * Evolution-line filter: widen or narrow whatever the other filters left,
 * along the one axis a dex filter could never reach before.
 *
 *   all    — no line handling; what matched is what shows.
 *   family — ALSO show every other stage of each matched species' line.
 *   base   — show only the matches nothing evolves into, plus the first
 *            evolution of a baby Pokémon (Pikachu as well as Pichu: trainers
 *            met Pikachu two generations before its baby and file it as the
 *            start of the line, so hiding it read as broken).
 *
 * Why it is worth its own axis: every other filter answers "which species",
 * and an evolution line is the one grouping a trainer thinks in that no
 * species-level predicate can express. Filter to Community Day 2025 and you
 * get the event species; what you actually want to go and catch is their whole
 * lines. Search "electabuzz" and you get one cell, when the thing you are
 * working on is Elekid -> Electabuzz -> Electivire.
 *
 * `family` DOES NOT re-apply the species-identity filters to what it adds —
 * that is the entire point. Electivire is not called "electabuzz" and was
 * never tagged into a 2025 Community Day, so re-testing the search or the
 * event against it would add nothing and the button would look broken. The
 * STATE axes (Caught/Missing, Released/Unreleased) do still apply afterwards,
 * because those are about you rather than about which species you asked for:
 * "Community Day 2025 + whole line + Missing" is the query worth having.
 *
 * `base` is subtractive only. It never swaps an evolved match for its base —
 * "slim this down" must not put a species on screen that the filters did not
 * match, so Missing + base on a set of pure evolutions is legitimately empty.
 */
export type LineFilter = "all" | "family" | "base";

export const LINE_FILTER_ORDER: LineFilter[] = ["all", "family", "base"];

export const LINE_FILTER_LABELS: Record<LineFilter, string> = {
  all: "Any",
  family: "Whole line",
  base: "Base only",
};

/**
 * Sort options for the grid.
 *   dex-asc / dex-desc   → by National Dex number
 *   name-asc / name-desc → by species name (A-Z / Z-A)
 *
 * The legacy "asc" | "desc" values are still accepted (and treated as
 * dex-asc / dex-desc) so older persisted UI state and the existing tests
 * keep working without a migration.
 */
export type SortOrder =
  | "asc" | "desc"
  | "dex-asc" | "dex-desc"
  | "name-asc" | "name-desc";

/** Normalise the legacy "asc"/"desc" aliases to the explicit dex sorts. */
export function normalizeSort(sort: SortOrder): Exclude<SortOrder, "asc" | "desc"> {
  if (sort === "asc") return "dex-asc";
  if (sort === "desc") return "dex-desc";
  return sort;
}

/** Minimal shape of a recorded entry (subset of DexEntryState). */
export interface RecordedEntryLite {
  dexNumber: number;
  state: CellState;
  source: "scan" | "manual" | "trade";
}

export interface DexGridFilterOptions {
  variant: PokedexVariant;
  recorded: RecordedEntryLite[];
  region: RegionFilter;
  status: StatusFilter;
  /**
   * Free-text search by dex number or species name (case-insensitive).
   * Comma-separated for a list: "25, 133, lapras" matches any of the three.
   */
  search: string;
  sort: SortOrder;
  /**
   * Obtainable-in-GO filter for the current variant. Optional so existing
   * callers (and the test suite) default to "all" and keep their behaviour.
   */
  availability?: AvailabilityFilter;
  /**
   * Pokémon type filter. Empty array means "no type filter". When one or
   * more types are selected an entry matches if EITHER of its types is in
   * the set (dual-typed Pokémon match on either type).
   */
  types?: PokeType[];
  /**
   * Event filter: the species tagged into the events the trainer picked
   * (a type × year cross, e.g. Community Day 2021 — see lib/eventCatalog).
   *
   * `undefined` / `null` means NO event filter. An EMPTY SET is a real filter
   * that matches nothing, and that distinction is the whole contract: picking a
   * type/year combination nobody has tagged species into must yield an empty
   * grid, because falling back to the full roster would read as "all 1025 were
   * in that event".
   */
  eventSpecies?: ReadonlySet<number> | null;
  /**
   * Forms filter — "has forms" / "has costumes". Optional so existing callers
   * (and the test suite) default to "all" and keep their behaviour.
   */
  forms?: FormFilter;
  /**
   * Evolution-line handling applied to whatever the other filters leave.
   * Optional so existing callers (and the test suite) default to "all".
   */
  line?: LineFilter;
  /**
   * Region-lock filter. Optional so existing callers (and the test suite)
   * default to "all".
   */
  exclusive?: ExclusiveFilter;
  /**
   * Species-class filter (Starter / Legendary / Mythical / Ultra Beast /
   * Pseudo). Empty or absent means no class filter; a non-empty selection
   * matches a species in ANY of the picked classes.
   */
  classes?: SpeciesClass[];
}

/**
 * The candidate set of dex numbers BEFORE region/status/search filtering.
 *
 * Roster policy (Push 4 / v1.4.3 b4):
 * - Variant has a curated GO-availability roster (mega / gmax): show that
 *   curated silhouette set, unioned with anything actually recorded. This
 *   is the fix for Mega/G-Max rendering "0 shown" on a fresh install —
 *   those tabs now show released Mega-/Gigantamax-capable species even
 *   before the user scans, instead of the empty recorded-only set.
 * - Other sparse variants (shadow / purified): only numbers we have
 *   recorded state for. Their in-game availability rotates too fast to
 *   maintain a curated list, so we keep the conservative recorded-only
 *   behaviour and never fabricate empties.
 * - Whole-roster variants (pokemon / shiny / lucky / perfect / xxs / xxl):
 *   the full National Dex 1..1025 unioned with anything recorded. These
 *   are user-attribute states overlaid on the complete species roster.
 */
export function baseDexNumbers(
  variant: PokedexVariant,
  recorded: RecordedEntryLite[],
): number[] {
  const set = new Set<number>();
  const curated = curatedRosterFor(variant);
  if (curated) {
    // Curated variants (mega / gmax / shadow / purified) have a fixed,
    // knowable GO roster. The curated list is the SOLE membership authority:
    // a cell appears only if it is a real member of that dex. Recorded
    // entries are overlaid as caught-state in computeDexGridNumbers but must
    // NOT add members here, otherwise a stray recorded id (e.g. a base
    // species mis-written into the mega/gmax store during a scan) would leak
    // in as a phantom caught cell showing the wrong species.
    for (const id of curated) {
      if (id >= 1 && id <= NATIONAL_DEX_MAX) set.add(id);
    }
    return Array.from(set);
  }
  // Non-curated variants overlay user state on a species roster, so recorded
  // ids ARE unioned (a scan-discovered species should appear).
  for (const e of recorded) {
    if (e.dexNumber >= 1 && e.dexNumber <= NATIONAL_DEX_MAX) set.add(e.dexNumber);
  }
  if (!isSparseVariant(variant)) {
    for (let i = 1; i <= NATIONAL_DEX_MAX; i++) set.add(i);
  }
  return Array.from(set);
}

function regionFilterFor(id: number): RegionFilter {
  return regionForId(id) ?? "unknown";
}

/**
 * Region display order, used so the dex-number sorts group by region and the
 * "Unidentified" region (Meltan #808 / Melmetal #809) renders at the very end
 * of the dex instead of mid-list at its dex number.
 *
 * For every id EXCEPT 808/809 the region rank is already monotonic with the dex
 * number (Kanto 1..151 → … → Paldea 906..1025), so ranking by region then dex
 * leaves the whole National Dex order unchanged and ONLY lifts 808/809 to the
 * tail. An id with no region (a stray out-of-range scan) ranks after everything.
 */
const REGION_SORT_ORDER: Region[] = [
  "kanto", "johto", "hoenn", "sinnoh", "unova", "kalos",
  "alola", "galar", "hisui", "paldea", "unidentified",
];

function regionRank(id: number): number {
  const r = regionForId(id);
  const rank = r ? REGION_SORT_ORDER.indexOf(r) : -1;
  return rank === -1 ? REGION_SORT_ORDER.length : rank;
}

/**
 * Split a raw search box value into terms on COMMAS.
 *
 * One box, many needles: "25, 133, lapras" is three searches OR-ed together.
 * A comma is the separator because that is the shape trainers already have a
 * list in — the dex's own "copy filtered numbers" button writes `1,4,7,25`, and
 * GO's in-game search takes the same, so a copied list pastes straight back in
 * here. Blank terms (trailing comma, double comma) are dropped so a half-typed
 * list never means "match nothing".
 */
export function parseSearchTerms(raw: string): string[] {
  return raw
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t.length > 0);
}

/**
 * One search term against one dex number.
 *
 * A purely numeric term (with an optional leading "#", and leading zeros
 * tolerated) is a DEX NUMBER and is matched EXACTLY. It used to be a substring
 * match, which made a list of numbers unusable: "1,4,7" would have pulled in
 * #1 plus #10-#19, #100-#199, #1000-#1025, #4x, #7x … — hundreds of cells
 * nobody asked for. Names stay a substring match, because names are typed
 * progressively ("chu" -> Pikachu, Raichu) and there is nothing to over-match.
 */
function matchesSearchTerm(id: number, term: string): boolean {
  const numeric = term.startsWith("#") ? term.slice(1) : term;
  if (/^\d+$/.test(numeric)) return Number(numeric) === id;
  if (!SPECIES_NAMES[id]) return false;
  return speciesName(id).toLowerCase().includes(term);
}

/**
 * Exported (2026-09-09) so the Potential Trades lead filter searches leads the
 * way the grid searches cells: exact on numbers, substring on names, commas
 * for a list. One search grammar for the whole app.
 */
export function matchesSearch(id: number, needle: string): boolean {
  const terms = parseSearchTerms(needle);
  if (terms.length === 0) return true;
  // OR across terms: an entry shows if ANY term matches it. (AND would be
  // empty by construction — no species is both #25 and #133.)
  return terms.some((t) => matchesSearchTerm(id, t));
}

/**
 * Type filter: an entry matches when the selected-type set is empty (no
 * filter) OR at least one of the entry's types is selected. Dual-typed
 * Pokémon therefore appear whenever EITHER of their types is chosen.
 */
function matchesTypes(id: number, selected: PokeType[] | undefined): boolean {
  if (!selected || selected.length === 0) return true;
  const entryTypes = typesFor(id);
  return entryTypes.some((t) => selected.includes(t));
}

function matchesForms(forms: FormFilter | undefined, id: number): boolean {
  if (!forms || forms === "all") return true;
  return forms === "costumes" ? hasCostumes(id) : hasAltForms(id);
}

function matchesExclusive(exclusive: ExclusiveFilter | undefined, id: number): boolean {
  if (!exclusive || exclusive === "all") return true;
  return exclusive === "exclusive" ? isRegionExclusive(id) : !isRegionExclusive(id);
}

function matchesStatus(
  status: StatusFilter,
  rec: RecordedEntryLite | undefined,
): boolean {
  switch (status) {
    case "all":
      return true;
    case "caught":
      return rec?.state === "present";
    case "missing":
      return !rec || rec.state !== "present";
  }
}

function matchesAvailability(
  availability: AvailabilityFilter | undefined,
  variant: PokedexVariant,
  id: number,
): boolean {
  if (!availability || availability === "all") return true;
  const released = isVariantAvailable(variant, id);
  return availability === "released" ? released : !released;
}

/**
 * Apply all view controls and return the final, sorted list of dex numbers
 * the grid should render.
 */
export function computeDexGridNumbers(opts: DexGridFilterOptions): number[] {
  const recordedByNumber = new Map<number, RecordedEntryLite>();
  for (const e of opts.recorded) recordedByNumber.set(e.dexNumber, e);

  const base = baseDexNumbers(opts.variant, opts.recorded);

  // The filters split in two, because the Line axis sits between them.
  //
  // IDENTITY axes say WHICH SPECIES you asked for (region, search, type,
  // forms, event, region-lock). STATE axes say what is true of you
  // (Caught/Missing) or of the game right now (Released/Unreleased). "Add
  // line" widens the identity result along evolution lines, then the state
  // axes narrow what comes out — see the LineFilter doc comment for why that
  // order is the useful one.
  //
  // Region-lock is identity, not state: it is a fact about the species, the
  // same for every trainer. So "Region locked + whole line" seeds on the
  // exclusives and then hands you their full lines, rather than filtering the
  // lines back down to the locked members.
  const matchesIdentity = (id: number): boolean => {
    if (opts.region !== "all" && regionFilterFor(id) !== opts.region) return false;
    if (!matchesSearch(id, opts.search)) return false;
    if (!matchesTypes(id, opts.types)) return false;
    if (!matchesForms(opts.forms, id)) return false;
    if (!matchesExclusive(opts.exclusive, id)) return false;
    if (!matchesSpeciesClasses(opts.classes, id)) return false;
    // Null/undefined = no event filter. An empty set is a real filter that
    // matches nothing — see the eventSpecies doc comment.
    if (opts.eventSpecies && !opts.eventSpecies.has(id)) return false;
    return true;
  };
  const matchesState = (id: number): boolean => {
    if (!matchesStatus(opts.status, recordedByNumber.get(id))) return false;
    if (!matchesAvailability(opts.availability, opts.variant, id)) return false;
    return true;
  };

  const identity = base.filter(matchesIdentity);
  const line = opts.line ?? "all";
  let widened: number[];
  if (line === "family" && identity.length > 0) {
    // Re-walk `base` rather than emitting the family ids directly: membership
    // of THIS dex stays the roster's call, so a curated variant (mega, gmax)
    // can never gain a phantom cell for a species that has no such form.
    const family = expandToFamilies(identity);
    widened = base.filter((id) => family.has(id));
  } else if (line === "base") {
    // "Base" as trainers mean it: the top of the line, plus a baby's first
    // evolution (Pikachu beside Pichu). See isBaseOrBabyEvolution.
    widened = identity.filter(isBaseOrBabyEvolution);
  } else {
    widened = identity;
  }

  const filtered = widened.filter(matchesState);

  const sort = normalizeSort(opts.sort);
  filtered.sort((a, b) => {
    switch (sort) {
      case "dex-desc":
        // Region-grouped, reversed: Unidentified (Meltan/Melmetal) leads,
        // then Paldea…Kanto, each region descending internally.
        return regionRank(b) - regionRank(a) || b - a;
      case "name-asc":
        return speciesName(a).localeCompare(speciesName(b)) || a - b;
      case "name-desc":
        return speciesName(b).localeCompare(speciesName(a)) || b - a;
      case "dex-asc":
      default:
        // Region-grouped ascending: identical to plain dex order for every id
        // except 808/809, which sort into the trailing Unidentified region.
        return regionRank(a) - regionRank(b) || a - b;
    }
  });
  return filtered;
}
